import React from 'react';
import { cn } from '@/lib/core/utils';
import { Card, CardContent, CardHeader } from '@/components/ui/card';

export type UnifiedSkeletonVariant =
  | 'page'      // Full page skeleton (Dashboard initial load)
  | 'tab'       // Tab content area skeleton
  | 'cards'     // Grid of card skeletons
  | 'list'      // List/table skeleton
  | 'form'      // Form fields skeleton
  | 'inline';   // Small inline spinner (for buttons)

interface UnifiedSkeletonProps {
  /** The type of skeleton to render */
  variant?: UnifiedSkeletonVariant;
  /** Number of rows/items to show (for list, form, cards variants) */
  rows?: number;
  /** Number of columns for cards variant */
  columns?: number;
  /** Additional CSS classes */
  className?: string;
  /** Whether to show header in card-based variants */
  showHeader?: boolean;
  /** Whether to animate (default: true) */
  animate?: boolean;
}

// Base skeleton block component
const SkeletonBlock = ({
  className,
  animate = true
}: {
  className?: string;
  animate?: boolean;
}) => (
  <div
    className={cn(
      "rounded-md bg-muted dark:bg-muted/50",
      animate && "animate-pulse",
      className
    )}
  />
);

// Page skeleton - for initial dashboard load (content area only)
const PageSkeleton = ({ className }: { className?: string }) => (
  <div className={cn("space-y-6", className)}>
    {/* Header */}
    <SkeletonBlock className="h-8 w-48" />

    {/* Stats cards row */}
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <SkeletonBlock className="h-32 rounded-xl" />
      <SkeletonBlock className="h-32 rounded-xl" />
      <SkeletonBlock className="h-32 rounded-xl" />
      <SkeletonBlock className="h-32 rounded-xl" />
    </div>

    {/* Main content area */}
    <SkeletonBlock className="h-64 rounded-xl" />
  </div>
);

/**
 * FullPageSkeleton - Complete page skeleton with navbar, sidebar, and content
 * Accurately matches the real GlobalLayout structure for seamless transitions
 */
export const FullPageSkeleton = () => (
  <div className="min-h-screen w-full bg-background flex">
    {/* Sidebar skeleton - hidden on mobile, matches real sidebar w-48 */}
    <div className="hidden md:flex w-48 flex-col border-r bg-sidebar">
      {/* Logo section - matches real sidebar logo positioning */}
      <div className="pl-3 pt-2.5">
        <div className="flex items-center gap-2 h-8">
          <SkeletonBlock className="size-6 rounded-md" />
          <SkeletonBlock className="h-4 w-16" />
        </div>
      </div>

      {/* Navigation items - matches real menu item heights h-8 */}
      <div className="flex-1 p-2 space-y-1">
        <SkeletonBlock className="h-8 w-full rounded-md" />
        <SkeletonBlock className="h-8 w-full rounded-md" />
        <SkeletonBlock className="h-8 w-full rounded-md" />
        <SkeletonBlock className="h-8 w-full rounded-md" />
        <SkeletonBlock className="h-8 w-3/4 rounded-md" />
      </div>

      {/* Footer toggles - AI toggle + Mode toggle */}
      <div className="p-2 flex items-center justify-center gap-1">
        <SkeletonBlock className="h-7 w-7 rounded-md" />
        <SkeletonBlock className="h-7 w-7 rounded-md" />
      </div>
    </div>

    {/* Main area - navbar + content */}
    <div className="flex-1 flex flex-col">
      {/* Navbar skeleton - h-12 matches real navbar */}
      <div className="h-12 border-b flex items-center">
        {/* Business selector (desktop) - w-48 h-12 with border-r */}
        <div className="hidden md:flex w-48 h-12 items-center px-2 border-r bg-sidebar">
          <SkeletonBlock className="h-4 w-32" />
        </div>
        {/* Mobile menu trigger */}
        <div className="md:hidden px-2">
          <SkeletonBlock className="h-8 w-8 rounded-md" />
        </div>
        {/* Spacer */}
        <div className="flex-1" />
        {/* Right icons - Settings, User, Theme */}
        <div className="flex items-center gap-2 px-2">
          <SkeletonBlock className="h-8 w-8 rounded-md" />
          <SkeletonBlock className="h-8 w-8 rounded-full" />
          <SkeletonBlock className="h-8 w-8 rounded-md" />
        </div>
      </div>

      {/* Content skeleton - matches real content padding p-6 */}
      <div className="flex-1 p-6 space-y-6">
        <SkeletonBlock className="h-8 w-48" />
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <SkeletonBlock className="h-32 rounded-xl" />
          <SkeletonBlock className="h-32 rounded-xl" />
          <SkeletonBlock className="h-32 rounded-xl" />
          <SkeletonBlock className="h-32 rounded-xl" />
        </div>
        <SkeletonBlock className="h-64 rounded-xl" />
      </div>
    </div>
  </div>
);

// Tab skeleton - for tab content loading
const TabSkeleton = ({ className, showHeader = true }: { className?: string; showHeader?: boolean }) => (
  <div className={cn("space-y-6", className)}>
    {showHeader && (
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <SkeletonBlock className="h-7 w-48" />
          <SkeletonBlock className="h-4 w-72" />
        </div>
        <div className="flex gap-2">
          <SkeletonBlock className="h-10 w-24" />
          <SkeletonBlock className="h-10 w-32" />
        </div>
      </div>
    )}

    {/* Content area */}
    <Card className="animate-pulse">
      <CardHeader className="space-y-0 pb-2">
        <SkeletonBlock className="h-5 w-32" />
      </CardHeader>
      <CardContent className="space-y-3">
        <SkeletonBlock className="h-4 w-full" />
        <SkeletonBlock className="h-4 w-3/4" />
        <SkeletonBlock className="h-4 w-1/2" />
      </CardContent>
    </Card>

    <Card className="animate-pulse">
      <CardContent className="pt-6 space-y-3">
        <SkeletonBlock className="h-40 w-full" />
      </CardContent>
    </Card>
  </div>
);

// Cards skeleton - grid of card placeholders
const CardsSkeleton = ({
  columns = 4,
  rows = 1,
  className
}: {
  columns?: number;
  rows?: number;
  className?: string;
}) => {
  const gridCols = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
  }[columns] || 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4';

  return (
    <div className={cn(`grid gap-4 ${gridCols}`, className)}>
      {Array.from({ length: columns * rows }).map((_, i) => (
        <Card key={i} className="animate-pulse">
          <CardHeader className="space-y-0 pb-2">
            <div className="flex items-center justify-between">
              <SkeletonBlock className="h-4 w-24" />
              <SkeletonBlock className="h-4 w-4" />
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            <SkeletonBlock className="h-8 w-16" />
            <SkeletonBlock className="h-3 w-20" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// List skeleton - table/list placeholders
const ListSkeleton = ({
  rows = 5,
  className
}: {
  rows?: number;
  className?: string;
}) => (
  <div className={cn("space-y-4", className)}>
    {/* Table header */}
    <div className="flex gap-4 px-4 py-2 border-b animate-pulse">
      <SkeletonBlock className="h-4 w-24" />
      <SkeletonBlock className="h-4 w-16 flex-1" />
      <SkeletonBlock className="h-4 w-20" />
      <SkeletonBlock className="h-4 w-24" />
      <SkeletonBlock className="h-4 w-16" />
    </div>

    {/* Table rows */}
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="flex gap-4 px-4 py-3 items-center animate-pulse">
        <div className="space-y-1">
          <SkeletonBlock className="h-4 w-32" />
          <SkeletonBlock className="h-3 w-24" />
        </div>
        <SkeletonBlock className="h-6 w-20 flex-1" />
        <SkeletonBlock className="h-6 w-24" />
        <SkeletonBlock className="h-8 w-28" />
        <div className="flex gap-2">
          <SkeletonBlock className="h-8 w-8" />
          <SkeletonBlock className="h-8 w-8" />
        </div>
      </div>
    ))}
  </div>
);

// Form skeleton - form field placeholders
const FormSkeleton = ({
  rows = 4,
  className
}: {
  rows?: number;
  className?: string;
}) => (
  <div className={cn("space-y-6", className)}>
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="space-y-2 animate-pulse">
        <SkeletonBlock className="h-4 w-24" />
        <SkeletonBlock className="h-10 w-full" />
      </div>
    ))}

    {/* Form buttons */}
    <div className="flex justify-end gap-2 pt-4 animate-pulse">
      <SkeletonBlock className="h-10 w-20" />
      <SkeletonBlock className="h-10 w-24" />
    </div>
  </div>
);

// Inline skeleton - small spinner for buttons/inline elements
const InlineSkeleton = ({ className }: { className?: string }) => (
  <div
    className={cn(
      "w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin",
      className
    )}
  />
);

/**
 * UnifiedSkeleton - A single component for all loading states
 *
 * Variants:
 * - 'page': Full page skeleton with header, stat cards, and content area
 * - 'tab': Tab content skeleton with optional header
 * - 'cards': Grid of card skeletons
 * - 'list': Table/list skeleton with rows
 * - 'form': Form fields skeleton
 * - 'inline': Small spinner for buttons
 *
 * @example
 * // Page loading
 * <UnifiedSkeleton variant="page" />
 *
 * // Tab content loading
 * <UnifiedSkeleton variant="tab" showHeader={false} />
 *
 * // Cards grid
 * <UnifiedSkeleton variant="cards" columns={3} rows={2} />
 *
 * // List with custom rows
 * <UnifiedSkeleton variant="list" rows={10} />
 *
 * // Button loading state
 * <Button disabled><UnifiedSkeleton variant="inline" /> Loading...</Button>
 */
export function UnifiedSkeleton({
  variant = 'tab',
  rows = 3,
  columns = 4,
  className,
  showHeader = true,
  animate = true,
}: UnifiedSkeletonProps) {
  switch (variant) {
    case 'page':
      return <PageSkeleton className={className} />;
    case 'tab':
      return <TabSkeleton className={className} showHeader={showHeader} />;
    case 'cards':
      return <CardsSkeleton columns={columns} rows={rows} className={className} />;
    case 'list':
      return <ListSkeleton rows={rows} className={className} />;
    case 'form':
      return <FormSkeleton rows={rows} className={className} />;
    case 'inline':
      return <InlineSkeleton className={className} />;
    default:
      return <TabSkeleton className={className} showHeader={showHeader} />;
  }
}

// Export sub-components for more granular use
export { SkeletonBlock, PageSkeleton, TabSkeleton, CardsSkeleton, ListSkeleton, FormSkeleton, InlineSkeleton };
